import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import { useTranslation } from 'react-i18next';
import { resendSpaCommunication } from '../APIs/SpaAPI';
import { COMMUNICATION_TARGETS, communicationResult, communicationStatusColor } from '../utils/spaCommunications';

export default function SpaCommunicationDeliveries({ items, token, tenant, canSend, capabilities, onChange }) {
  const { t, i18n } = useTranslation();
  const ct = (key, options) => t(`spa.communications.${key}`, options);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const active = useRef(false);
  const lock = useRef(false);
  const scope = `${token}:${tenant}:${canSend}`;
  const currentScope = useRef(scope); currentScope.current = scope;
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  const date = (value) => {
    const parsed = value && new Date(value);
    return parsed && !Number.isNaN(parsed.getTime()) ? new Intl.DateTimeFormat(i18n.resolvedLanguage || i18n.language || 'en', {
      dateStyle: 'medium', timeStyle: 'short', ...(capabilities?.timeZone ? { timeZone: capabilities.timeZone } : {}),
    }).format(parsed) : '-';
  };
  const eligible = (item) => canSend && item.channel === 'EMAIL' && capabilities?.channels?.EMAIL?.ready === true
    && ['SENT', 'FAILED'].includes(item.status);
  const resend = async () => {
    if (!selected || !eligible(selected) || lock.current || !token || !tenant) return;
    const submittedScope = scope;
    lock.current = true; setSaving(true); setError(''); setResult(null);
    try {
      const response = communicationResult(await resendSpaCommunication(selected.id, token, tenant));
      if (!active.current || currentScope.current !== submittedScope) return;
      if (!response) throw new Error(ct('invalidResponse'));
      setResult(response); onChange?.();
    } catch (failure) {
      if (active.current && currentScope.current === submittedScope) setError(failure.message || ct('sendError'));
    } finally {
      lock.current = false;
      if (active.current && currentScope.current === submittedScope) setSaving(false);
    }
  };
  return <Stack spacing={2}>
    {items.map((item) => <Box key={item.id} sx={{ borderBottom: 1, borderColor: 'divider', pb: 2, overflowWrap: 'anywhere' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <Typography fontWeight={700}>{ct(`events.${item.event}`, { defaultValue: item.event || '-' })}</Typography>
            <Chip size="small" color={communicationStatusColor(item.status)} label={ct(`statuses.${item.status}`, { defaultValue: item.status })} />
            <Typography variant="caption">{ct(`channels.${item.channel}`, { defaultValue: item.channel || '-' })} / {item.language || '-'}</Typography>
          </Stack>
          <Typography>{item.recipient || '-'}</Typography>
          {item.subject && <Typography>{item.subject}</Typography>}
          <Typography variant="caption">
            #{item.id}{item.clientId ? ` | ${ct('clientId')}: ${item.clientId}` : ''}
            {Object.keys(COMMUNICATION_TARGETS).filter((key) => item[key]).map((key) => ` | ${ct(`targets.${key}`)} #${item[key]}`).join('')}
          </Typography>
          <Typography variant="caption">{ct('createdAt')}: {date(item.createdAt)}</Typography>
          {item.attemptedAt && <Typography variant="caption">{ct('attemptedAt')}: {date(item.attemptedAt)}</Typography>}
          {item.sentAt && <Typography variant="caption">{ct('sentAt')}: {date(item.sentAt)}</Typography>}
          {item.attempts != null && <Typography variant="caption">{ct('attempts')}: {item.attempts}</Typography>}
          {item.nextAttemptAt && <Typography variant="caption">{ct('nextAttemptAt')}: {date(item.nextAttemptAt)}</Typography>}
          {item.errorCode && <Typography variant="body2" color="text.secondary">{ct(`reasons.${item.errorCode}`, { defaultValue: item.errorCode })}</Typography>}
          {item.error && <Typography color={item.status === 'FAILED' ? 'error' : 'text.secondary'} sx={{ whiteSpace: 'pre-wrap' }}>{item.error}</Typography>}
        </Stack>
        {canSend && <Box><Button startIcon={<ReplayIcon />} disabled={!eligible(item) || saving}
          onClick={() => { setSelected(item); setError(''); setResult(null); }}>{ct('resend')}</Button></Box>}
      </Stack>
    </Box>)}
    <Dialog open={Boolean(selected) && canSend} onClose={() => !saving && setSelected(null)} fullWidth maxWidth="sm">
      <DialogTitle>{ct('resendTitle')}</DialogTitle>
      <DialogContent><Stack spacing={2}>
        <Typography>{selected?.recipient}</Typography>
        <Alert severity="warning">{ct('resendConfirm')}</Alert>
        {selected?.nextAttemptAt && <Alert severity="warning">{ct('retryScheduledWarning')} {date(selected.nextAttemptAt)}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
        {result && <Alert severity={result.status === 'SENT' ? 'success' : 'info'}>{ct(`outcomes.${result.status}`)}</Alert>}
      </Stack></DialogContent>
      <DialogActions><Button disabled={saving} onClick={() => setSelected(null)}>{ct(result ? 'close' : 'cancel')}</Button>
        {!result && <Button variant="contained" startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <ReplayIcon />}
          disabled={saving || !selected || !eligible(selected)} onClick={resend}>{ct('confirmResend')}</Button>}
      </DialogActions>
    </Dialog>
  </Stack>;
}
